import { getAuthToken } from '../tokenService';
import { getApiBaseUrl } from './config';
import { getErrorMessage } from '../../utils/errorUtils';
import { ERROR_MESSAGES } from '../../constants';

const RATE_LIMIT_MAX_RETRIES = 3;
const RATE_LIMIT_BASE_DELAY_MS = 1000;
const RATE_LIMIT_MAX_DELAY_MS = 30000;

/**
 * Response wrapper that includes ETag and status metadata.
 * Used by the caching layer for stale-while-revalidate.
 */
export interface ApiResponse<T> {
  data: T | undefined;
  etag: string | null;
  status: number;
  notModified: boolean;
}

/**
 * Builds a full API URL with optional query parameters.
 * Undefined/null values are omitted. Arrays are joined with commas.
 */
export const buildUrl = (
  path: string,
  params?: Record<string, string | number | string[] | undefined | null>
): string => {
  const base = `${getApiBaseUrl()}${path}`;
  if (!params) return base;

  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      if (value.length > 0) searchParams.set(key, value.join(','));
    } else {
      searchParams.set(key, String(value));
    }
  }

  const qs = searchParams.toString();
  return qs ? `${base}?${qs}` : base;
};

/**
 * Handles API errors consistently
 */
export const handleApiError = (message: string, error: unknown): never => {
  const detail = getErrorMessage(error, '');
  const errorMsg = detail ? `${message}: ${detail}` : message;
  console.error(errorMsg, error);

  // Check for rate limiting
  if (errorMsg.includes('Too many requests')) {
    throw new Error(ERROR_MESSAGES.RATE_LIMITED);
  }

  // Check if this is a 404 error - treat as "no albums exist"
  if (errorMsg.includes('Status: 404')) {
    console.log('404 error detected - treating as no albums exist');
    throw new Error('Not found');
  }

  // For other errors, throw to show error modal
  throw new Error(errorMsg);
};

/**
 * Wraps an async API call with consistent error handling.
 * Catches errors and delegates to handleApiError (which always throws).
 */
export const safeApiCall = async <T>(
  fn: () => Promise<T>,
  errorMessage: string
): Promise<T> => {
  try {
    return await fn();
  } catch (error) {
    return handleApiError(errorMessage, error);
  }
};

/**
 * Makes an authenticated API request with automatic retry on 429 (rate limited)
 */
export const apiRequest = async <T>(
  url: string,
  options: RequestInit = {}
): Promise<T> => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Authentication required');
  }

  for (let attempt = 0; attempt <= RATE_LIMIT_MAX_RETRIES; attempt++) {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });

    if (response.status === 429 && attempt < RATE_LIMIT_MAX_RETRIES) {
      const retryAfterHeader = response.headers.get('Retry-After');
      const retryAfterSeconds = retryAfterHeader ? parseInt(retryAfterHeader, 10) : NaN;
      const delay = !isNaN(retryAfterSeconds)
        ? retryAfterSeconds * 1000
        : Math.min(RATE_LIMIT_BASE_DELAY_MS * Math.pow(2, attempt) + Math.random() * 500, RATE_LIMIT_MAX_DELAY_MS);
      console.warn(`Rate limited (429). Retrying in ${Math.round(delay)}ms (attempt ${attempt + 1}/${RATE_LIMIT_MAX_RETRIES})`);
      await new Promise(resolve => setTimeout(resolve, delay));
      continue;
    }

    if (!response.ok) {
      if (response.status === 429) {
        throw new Error(ERROR_MESSAGES.RATE_LIMITED);
      }
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    // Handle empty responses (204 No Content, or empty body)
    const contentLength = response.headers.get('content-length');
    if (response.status === 204 || contentLength === '0') {
      return undefined as unknown as T;
    }

    const text = await response.text();
    if (!text) {
      return undefined as unknown as T;
    }

    return JSON.parse(text) as T;
  }

  throw new Error(ERROR_MESSAGES.RATE_LIMITED);
};

/**
 * Makes an authenticated API request that supports ETag-based caching.
 * Sends If-None-Match when an etag is provided, and returns 304 status
 * as { data: undefined, notModified: true } instead of throwing.
 */
export const apiRequestWithETag = async <T>(
  url: string,
  etag?: string | null,
  options: RequestInit = {}
): Promise<ApiResponse<T>> => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Authentication required');
  }

  const headers: Record<string, string> = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>)
  };

  if (etag) {
    headers['If-None-Match'] = etag;
  }

  for (let attempt = 0; attempt <= RATE_LIMIT_MAX_RETRIES; attempt++) {
    const response = await fetch(url, {
      ...options,
      headers
    });

    if (response.status === 429 && attempt < RATE_LIMIT_MAX_RETRIES) {
      const retryAfterHeader = response.headers.get('Retry-After');
      const retryAfterSeconds = retryAfterHeader ? parseInt(retryAfterHeader, 10) : NaN;
      const delay = !isNaN(retryAfterSeconds)
        ? retryAfterSeconds * 1000
        : Math.min(RATE_LIMIT_BASE_DELAY_MS * Math.pow(2, attempt) + Math.random() * 500, RATE_LIMIT_MAX_DELAY_MS);
      console.warn(`Rate limited (429). Retrying in ${Math.round(delay)}ms (attempt ${attempt + 1}/${RATE_LIMIT_MAX_RETRIES})`);
      await new Promise(resolve => setTimeout(resolve, delay));
      continue;
    }

    // Handle 304 Not Modified
    if (response.status === 304) {
      return {
        data: undefined,
        etag: response.headers.get('etag') || etag || null,
        status: 304,
        notModified: true
      };
    }

    if (!response.ok) {
      if (response.status === 429) {
        throw new Error(ERROR_MESSAGES.RATE_LIMITED);
      }
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const responseEtag = response.headers.get('etag');

    // Handle empty responses (204 No Content, or empty body)
    const contentLength = response.headers.get('content-length');
    if (response.status === 204 || contentLength === '0') {
      return {
        data: undefined as unknown as T,
        etag: responseEtag,
        status: response.status,
        notModified: false
      };
    }

    const text = await response.text();
    if (!text) {
      return {
        data: undefined as unknown as T,
        etag: responseEtag,
        status: response.status,
        notModified: false
      };
    }

    return {
      data: JSON.parse(text) as T,
      etag: responseEtag,
      status: response.status,
      notModified: false
    };
  }

  throw new Error(ERROR_MESSAGES.RATE_LIMITED);
};

/**
 * Makes an authenticated POST request
 */
export const apiPost = async <T>(
  url: string,
  body: unknown
): Promise<T> => {
  return apiRequest<T>(url, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

/**
 * Makes an authenticated PATCH request
 */
export const apiPatch = async <T>(
  url: string,
  body: unknown
): Promise<T> => {
  return apiRequest<T>(url, {
    method: 'PATCH',
    body: JSON.stringify(body)
  });
};

/**
 * Makes an authenticated PUT request
 */
export const apiPut = async <T>(
  url: string,
  body: unknown
): Promise<T> => {
  return apiRequest<T>(url, {
    method: 'PUT',
    body: JSON.stringify(body)
  });
};

/**
 * Makes an authenticated DELETE request
 */
export const apiDelete = async (url: string): Promise<void> => {
  await apiRequest<void>(url, { method: 'DELETE' });
};

/**
 * Maps the data inside an ApiResponse without disturbing the etag/status/notModified
 * envelope. Useful when the raw API shape needs to be transformed before caching.
 */
export const mapApiResponse = <TIn, TOut>(
  response: ApiResponse<TIn>,
  transform: (data: TIn) => TOut
): ApiResponse<TOut> => {
  if (response.notModified || response.data === undefined) {
    return {
      data: undefined,
      etag: response.etag,
      status: response.status,
      notModified: response.notModified,
    };
  }
  return {
    data: transform(response.data),
    etag: response.etag,
    status: response.status,
    notModified: false,
  };
};
